import { queryOptions } from "@tanstack/react-query";
import {
  copyOrganizationExercise,
  createOrganizationExercise,
  deleteOrganizationExercise,
  getOrganizationExercise,
  listOrganizationExercises,
  removeOrganizationExerciseImage,
  updateOrganizationExercise,
  uploadOrganizationExerciseImage,
  uploadOrganizationExerciseVideo,
} from "api-client";
import { getMuscleNamesByGroups } from "utils";

import {
  type CreateExerciseInput,
  type ExerciseList,
  type ExerciseDetail,
  type ExerciseListInput,
  type ExercisesRepository,
  type UpdateExerciseInput,
} from "./ExercisesRepository";

export function createOrganizationExercisesRepository(organizationId: string) {
  const rootKey = ["exercises", "organization", organizationId] as const;

  async function list(input: ExerciseListInput) {
    const muscleNames = getMuscleNamesByGroups(input.muscleGroup ?? []);
    const response = await listOrganizationExercises({
      path: { organizationId },
      query: {
        pageIndex: input.pageIndex,
        pageSize: input.pageSize,
        query: input.q,
        showOwned: input.showOwned?.toString(),
        showGlobal: input.showGlobal?.toString(),
        muscles:
          input.muscles ?? (muscleNames.length > 0 ? muscleNames : undefined),
        categories: input.category,
        movementPatterns: input.movementPattern,
      },
    });
    return response.data.data;
  }

  const repository = {
    rootKey,
    list,
    listOptions(input: ExerciseListInput) {
      return queryOptions<ExerciseList>({
        queryKey: [...rootKey, "list", input],
        queryFn: () => list(input),
      });
    },
    detailOptions(exerciseId: string) {
      return queryOptions<ExerciseDetail>({
        queryKey: [...rootKey, "detail", exerciseId],
        async queryFn() {
          const response = await getOrganizationExercise({
            path: { organizationId, exerciseId },
          });
          return response.data.data;
        },
      });
    },
    async createExercise(input: CreateExerciseInput) {
      const response = await createOrganizationExercise({
        path: { organizationId },
        body: input,
      });
      return response.data.data;
    },
    async copyExercise(exerciseId: string) {
      const response = await copyOrganizationExercise({
        path: { organizationId, exerciseId },
      });
      return response.data.data;
    },
    deleteExercise(exerciseId: string) {
      return deleteOrganizationExercise({
        path: { organizationId, exerciseId },
      });
    },
    async updateExercise(input: UpdateExerciseInput) {
      const { id, ...body } = input;
      return updateOrganizationExercise({
        path: { organizationId, exerciseId: id },
        body,
      });
    },
    async uploadExerciseImage(exerciseId: string, file: File) {
      const response = await uploadOrganizationExerciseImage({
        path: { organizationId, exerciseId },
        body: { file },
      });
      return response.data.data;
    },
    async uploadExerciseVideo(exerciseId: string, file: File) {
      const response = await uploadOrganizationExerciseVideo({
        path: { organizationId, exerciseId },
        body: { file },
      });
      return response.data.data;
    },
    async removeExerciseImage(exerciseId: string, imageId: string) {
      return removeOrganizationExerciseImage({
        path: { organizationId, exerciseId, imageId },
      });
    },
  };

  return repository satisfies ExercisesRepository;
}
